// Ported from mstreetinvest@cec8be916:resources/js/components/crm-chat/cards/bar-list.tsx - as is (PORTING.md §1.2).
// One edit (vCRO wave 4, 2026-10-03): an optional `renderLabel`, so a surface whose labels are user text (the tenant
// chat's department names) can isolate them; without it the label renders exactly as before.
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { formatCount } from './format';

export interface BarListRow {
    label: string;
    /** One value per series, in series order. */
    values: number[];
}

/**
 * Horizontal bars for comparing categories: the label, a flat square bar per series on one shared scale, and each
 * value at the end of its bar, so every number is readable without hovering. With several series each category
 * gets a thin bar per series (the caller shows the legend).
 */
export function BarList({
    rows,
    colors = ['var(--chart-1)'],
    formatValue = formatCount,
    renderLabel,
}: {
    rows: BarListRow[];
    colors?: string[];
    formatValue?: (value: number) => string;
    /** How a label is shown (the tenant chat wraps user text in <UserText>); the plain string when absent. */
    renderLabel?: (label: string) => ReactNode;
}) {
    const max = Math.max(0, ...rows.flatMap((row) => row.values));
    const grouped = rows.some((row) => row.values.length > 1);

    return (
        <ul
            className={cn(
                'flex flex-col px-3 py-3',
                grouped ? 'gap-3' : 'gap-2',
            )}
        >
            {rows.map((row, index) => (
                <li
                    key={`${row.label}-${index}`}
                    className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] items-center gap-3 @md:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]"
                >
                    <span
                        title={row.label}
                        className={cn(
                            'truncate text-xs text-foreground',
                            grouped && 'self-start leading-4',
                        )}
                    >
                        {renderLabel ? renderLabel(row.label) : row.label}
                    </span>
                    <div className="flex min-w-0 flex-col gap-0.5">
                        {row.values.map((value, seriesIndex) => {
                            const share = max > 0 ? (value / max) * 100 : 0;

                            return (
                                <div
                                    key={seriesIndex}
                                    className="grid grid-cols-[minmax(0,1fr)_minmax(2.5rem,auto)] items-center gap-3"
                                >
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            'block',
                                            grouped ? 'h-2.5' : 'h-4',
                                        )}
                                    >
                                        <span
                                            className="block h-full"
                                            style={{
                                                width:
                                                    value > 0
                                                        ? `max(2px, ${share}%)`
                                                        : 0,
                                                backgroundColor:
                                                    colors[seriesIndex] ??
                                                    colors[0],
                                            }}
                                        />
                                    </span>
                                    <span
                                        className={cn(
                                            'text-end text-foreground tabular-nums',
                                            grouped
                                                ? 'text-[11px] leading-3'
                                                : 'text-xs font-medium',
                                        )}
                                    >
                                        {formatValue(value)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </li>
            ))}
        </ul>
    );
}
